import { useEffect, useRef, useState } from "react";
import PosAPI from "../api/inventory/posApi";
import { useDebounce } from "../hooks/useDebounce";
import { showError } from "../utils/toastUtils";
import { inr } from "../utils/posBilling";

const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const isTypingField = (el) =>
  el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);

/**
 * POS quick search:
 *  - Type -> dropdown (arrow keys + Enter to select)
 *  - Barcode / QR scanner -> exact SKU match turant cart me (dropdown ka wait nahi)
 *  - Input focused na ho tab bhi scanner kaam karta hai (global listener)
 *
 * onSelect(row)          -> row = { productId, variantId, title, variantName, sku, price, mrp, stock, gst, unit, thumbnail }
 * onPickVariant(product) -> multi-variant product (product SKU / QR scan) ke liye variant picker
 */
export default function PosProductSearch({ onSelect, onPickVariant }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [resultsFor, setResultsFor] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const reqIdRef = useRef(0);
  const debounced = useDebounce(query.trim(), 250);

  /* ---------------- SEARCH (typing) ---------------- */
  useEffect(() => {
    if (!debounced) {
      setResults([]);
      setResultsFor("");
      setOpen(false);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);

    PosAPI.search(debounced)
      .then((res) => {
        if (reqId !== reqIdRef.current) return; // purana response ignore
        if (res.code === 200) {
          setResults(res.data || []);
          setResultsFor(debounced);
          setHighlight(0);
          setOpen(true);
        }
      })
      .catch(() => reqId === reqIdRef.current && setResults([]))
      .finally(() => reqId === reqIdRef.current && setLoading(false));
  }, [debounced]);

  /* ---------------- CLOSE ON OUTSIDE CLICK ---------------- */
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* Highlighted row ko scroll me visible rakho */
  useEffect(() => {
    listRef.current?.children?.[highlight]?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const clear = () => {
    reqIdRef.current++; // pending search cancel
    setQuery("");
    setResults([]);
    setResultsFor("");
    setOpen(false);
    setLoading(false);
    inputRef.current?.focus();
  };

  const select = (row) => {
    onSelect(row);
    clear();
  };

  /* ---------------- EXACT CODE (scan) ---------------- */
  const lookup = async (code) => {
    setLoading(true);
    try {
      const res = await PosAPI.lookup(code);
      if (res.code === 200) {
        if (res.data.type === "variant") onSelect(res.data.item);
        else onPickVariant(res.data.product);
        clear();
      } else {
        showError(res.message || `No product found for "${code}"`);
      }
    } catch (err) {
      showError(errMsg(err, `No product found for "${code}"`));
      inputRef.current?.select();
    } finally {
      setLoading(false);
    }
  };

  const lookupRef = useRef(lookup);
  lookupRef.current = lookup;

  /* ---------------- GLOBAL SCANNER (input focus ke bina) ---------------- */
  useEffect(() => {
    let buffer = "";
    let last = 0;

    const onKey = (e) => {
      if (isTypingField(e.target)) return; // input me type ho raha hai to input ka handler sambhalega
      const now = Date.now();

      if (e.key === "Enter") {
        // scanner bahut fast type karta hai (< 50ms per char)
        if (buffer.length >= 3 && now - last < 100) {
          e.preventDefault();
          lookupRef.current(buffer);
        }
        buffer = "";
        return;
      }
      if (e.key.length !== 1) return;
      if (now - last > 60) buffer = "";
      buffer += e.key;
      last = now;
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  /* ---------------- KEYBOARD ---------------- */
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open && results.length) setOpen(true);
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const code = query.trim();
      if (!code) return;

      const exact = results.find((r) => (r.sku || "").toLowerCase() === code.toLowerCase());
      if (exact) return select(exact);

      // dropdown isi text ke results dikha raha hai -> highlighted row
      if (open && results.length && resultsFor === code) return select(results[highlight]);

      // scanner ne type karke Enter maar diya, search abhi aaya hi nahi -> exact lookup
      lookup(code);
    }
  };

  return (
    <div className="position-relative pos-search" ref={containerRef}>
      <div className="input-group">
        <span className="input-group-text">
          <i className="ti ti-barcode" />
        </span>
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          placeholder="Search by product name or SKU, or scan barcode / QR"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          autoFocus
          autoComplete="off"
        />
        {loading ? (
          <span className="input-group-text">
            <span className="spinner-border spinner-border-sm" />
          </span>
        ) : (
          query && (
            <button type="button" className="btn btn-outline-secondary" onClick={clear} title="Clear">
              <i className="ti ti-x" />
            </button>
          )
        )}
      </div>

      {open && results.length > 0 && (
        <ul className="list-group position-absolute w-100 shadow pos-search-list" ref={listRef}>
          {results.map((r, idx) => {
            const out = r.stock <= 0;
            return (
              <li
                key={r.variantId}
                className={`list-group-item list-group-item-action ${idx === highlight ? "active" : ""} ${out ? "is-out" : ""}`}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => e.preventDefault()} // input ka focus na jaaye
                onClick={() => select(r)}
              >
                <div className="d-flex align-items-center gap-2">
                  <span className="pos-search-thumb">
                    {r.thumbnail ? <img src={r.thumbnail} alt="" /> : <i className="ti ti-photo" />}
                  </span>
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="fw-semibold text-truncate">
                      {r.title}
                      {r.variantName && <span className="fw-normal"> — {r.variantName}</span>}
                    </div>
                    <div className="small">
                      SKU: <code>{r.sku}</code>
                    </div>
                  </div>
                  <div className="text-end ms-2 text-nowrap">
                    <div className="fw-semibold">{inr(r.price)}</div>
                    <span className={`badge ${out ? "bg-danger" : r.stock <= 5 ? "bg-warning" : "bg-success"}`}>
                      {out ? "Out of stock" : `${r.stock} in stock`}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {open && !loading && resultsFor && results.length === 0 && (
        <div className="position-absolute w-100 shadow bg-white border rounded p-3 text-center text-muted small pos-search-list">
          No products found for "{resultsFor}". Press Enter to look up the exact code.
        </div>
      )}
    </div>
  );
}